import { Box } from '@chakra-ui/react';
import { CustomOnResize } from 'modules/Editor/components/BoundingBox/BoundingBox.types';
import { ReactElement, useCallback, useEffect, useId, useState } from 'react';
import { ReadOnlyWidgetBase, TransparentImg, WidgetBase, WidgetBaseProp, WidgetToolbar, useWidget } from 'widgets/sdk';
import { responsiveShapeResize } from 'widgets/store';

import { generateShapeStyle } from './BasicShapeWidget.helpers';
import { multiEditConfig } from './BasicShapeWidget.multiEdit';
import { BasicShapeLayoutProps, BasicShapeWidgetData } from './BasicShapeWidget.types';
import { BasicShapeWidgetToolbarMenu } from './BasicShapeWidgetToolbar';
import { SHAPE_RESIZE_MAP, SHAPE_TYPE_MAP } from './Shapes';

const COMPLEX_SHAPES_INDENT_SIZE = 31;

export const BasicShapeWidget = (): ReactElement => {
  // the id needs to be unique per component instance - as there can be multiple of the same widget on the same page -  widgetId will clash
  const id = useId();
  const { widthPx, heightPx, border, type, widgetId } = useWidget<BasicShapeWidgetData>();
  const [dimension, setDimension] = useState({ width: widthPx, height: heightPx });

  useEffect(() => {
    setDimension({ width: widthPx, height: heightPx });
  }, [widthPx, heightPx]);

  const resizeBasicShape = useCallback(
    (widthPx: number, heightPx: number) => {
      if (SHAPE_RESIZE_MAP[type]) {
        SHAPE_RESIZE_MAP[type]({
          widthPx,
          heightPx,
          borderWidth: border.width,
          id,
          offset: COMPLEX_SHAPES_INDENT_SIZE,
        });
      }
    },
    [border.width, type, id],
  );

  // Add widget specific resize function for resizing the shape outside of this component
  // (e.g. for responsive groups)
  useEffect(() => {
    responsiveShapeResize.addResizeFn(widgetId, resizeBasicShape);

    return () => {
      responsiveShapeResize.clear(widgetId);
    };
  }, [widgetId, resizeBasicShape]);

  const customOnResize = useCallback(
    ({ event, onResize }: CustomOnResize) => {
      onResize(event);

      const widthPx = event.target.clientWidth;
      const heightPx = event.target.clientHeight;

      // resize the shape without setting the new dimensions to the state
      resizeBasicShape(widthPx, heightPx);
    },
    [resizeBasicShape],
  );

  return (
    <WidgetBase onResize={customOnResize}>
      <WidgetToolbar multiEditConfig={multiEditConfig}>
        <BasicShapeWidgetToolbarMenu />
      </WidgetToolbar>
      <BasicShapeLayout id={id} dimension={dimension} offset={COMPLEX_SHAPES_INDENT_SIZE} />
    </WidgetBase>
  );
};

export const ReadOnlyBasicShapeWidget = ({ includeAltTextImg }: WidgetBaseProp): ReactElement => {
  // the id needs to be unique per component instance - as there can be multiple of the same widget on the same page -  widgetId will clash
  const id = useId();
  const { widthPx, heightPx } = useWidget<BasicShapeWidgetData>();
  const dimension = { width: widthPx, height: heightPx };

  return (
    <ReadOnlyWidgetBase>
      <BasicShapeLayout id={id} dimension={dimension} offset={COMPLEX_SHAPES_INDENT_SIZE} isReadOnly={true} />
      {includeAltTextImg && <TransparentImg />}
    </ReadOnlyWidgetBase>
  );
};

const BasicShapeLayout = ({ dimension, offset, isReadOnly, id }: BasicShapeLayoutProps): ReactElement => {
  const { type, border, fillColor, fillPercent, mirror, cornerRadius } = useWidget<BasicShapeWidgetData>();
  const { width, height } = dimension;
  const Shape = SHAPE_TYPE_MAP[type];

  const style = generateShapeStyle(mirror);

  return (
    <Box w={'100%'} h={'100%'} position={'absolute'}>
      <Shape
        id={id}
        fillColor={fillColor}
        fillPercent={fillPercent}
        widthPx={width}
        heightPx={height}
        border={border}
        style={style}
        offset={offset}
        isReadOnly={isReadOnly}
        {...(cornerRadius && { cornerRadius })}
      />
    </Box>
  );
};
